import { useMemo, useState } from 'react';
import { generateTerraform } from '../../lib/terraform/generate';
import { toDesignNode, toDesignEdge } from '../../lib/designUtils';
import { generateTfvars, generateVariables, ENVIRONMENTS } from '../../lib/designer/environments';
import { generateModuleWrapper } from '../../lib/designer/moduleWrapper';

function TerraformPreview({ nodes, edges }) {
  const [mode, setMode] = useState('flat');
  const [view, setView] = useState('main');
  const [envId, setEnvId] = useState('dev');
  const [moduleFileIdx, setModuleFileIdx] = useState(0);

  const hclResult = useMemo(() => {
    const designNodes = nodes.map(toDesignNode);
    const designEdges = edges.map(toDesignEdge);
    return generateTerraform(designNodes, designEdges);
  }, [nodes, edges]);

  const moduleResult = useMemo(() => {
    if (nodes.length === 0) return { ok: true, files: [] };
    return generateModuleWrapper(nodes, edges, 'landing_zone');
  }, [nodes, edges]);

  const tfvars = useMemo(() => {
    if (nodes.length === 0) return '';
    return generateTfvars(nodes, envId);
  }, [nodes, envId]);

  const variables = useMemo(() => {
    if (nodes.length === 0) return '';
    return generateVariables(nodes);
  }, [nodes]);

  if (nodes.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <p className="text-center text-xs text-gray-500 dark:text-gray-400">
          Add nodes to the canvas to generate Terraform HCL
        </p>
      </div>
    );
  }

  if (!hclResult.ok && mode === 'flat' && view === 'main') {
    return (
      <div className="p-3">
        <p className="mb-2 text-xs font-semibold text-red-500 dark:text-red-400">
          Validation errors
        </p>
        <ul className="space-y-1">
          {hclResult.errors.map((err, index) => (
            <li key={index} className="text-xs text-red-500 dark:text-red-400">
              &bull; {err}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const modeBtnClass = (m) => `text-xs px-2 py-1 rounded transition-colors ${
    mode === m
      ? 'bg-gray-700 dark:bg-gray-600 text-white'
      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
  }`;

  const subTabClass = (tab) => `text-xs px-2 py-1 rounded transition-colors ${
    view === tab
      ? 'bg-teal-500 text-white'
      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
  }`;

  let content = '';
  let moduleFiles = [];

  if (mode === 'flat') {
    content = hclResult.hcl || '';
    if (view === 'tfvars') content = tfvars;
    if (view === 'variables') content = variables;
  } else {
    moduleFiles = moduleResult.ok ? moduleResult.files : [];
    if (moduleFiles.length > 0) {
      const idx = Math.min(moduleFileIdx, moduleFiles.length - 1);
      content = moduleFiles[idx]?.content || '';
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 p-2 border-b border-gray-200 dark:border-gray-700 shrink-0">
        <button className={modeBtnClass('flat')} onClick={() => { setMode('flat'); setView('main'); }}>Flat</button>
        <button className={modeBtnClass('module')} onClick={() => { setMode('module'); setModuleFileIdx(0); }}>Module</button>
      </div>

      {mode === 'flat' && (
        <div className="flex items-center gap-1 p-2 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <button className={subTabClass('main')} onClick={() => setView('main')}>main.tf</button>
          <button className={subTabClass('variables')} onClick={() => setView('variables')}>variables.tf</button>
          <button className={subTabClass('tfvars')} onClick={() => setView('tfvars')}>.tfvars</button>
          {view === 'tfvars' && (
            <select
              value={envId}
              onChange={(e) => setEnvId(e.target.value)}
              className="ml-auto h-6 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-1"
            >
              {ENVIRONMENTS.map((env) => (
                <option key={env.id} value={env.id}>{env.name}</option>
              ))}
            </select>
          )}
        </div>
      )}

      {mode === 'module' && (
        <div className="flex items-center gap-1 p-2 border-b border-gray-200 dark:border-gray-700 shrink-0 flex-wrap">
          {moduleFiles.map((f, i) => (
            <button
              key={f.path}
              className={`text-xs px-2 py-1 rounded transition-colors ${
                i === moduleFileIdx
                  ? 'bg-teal-500 text-white'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
              onClick={() => setModuleFileIdx(i)}
              title={f.path}
            >
              {f.path.split('/').pop()}
            </button>
          ))}
        </div>
      )}

      {mode === 'module' && !moduleResult.ok && (
        <div className="p-3">
          <p className="mb-2 text-xs font-semibold text-red-500 dark:text-red-400">Validation errors</p>
          <ul className="space-y-1">
            {moduleResult.errors?.map((err, i) => (
              <li key={i} className="text-xs text-red-500 dark:text-red-400">&bull; {err}</li>
            ))}
          </ul>
        </div>
      )}

      <pre className="flex-1 overflow-auto p-3 font-mono text-xs leading-relaxed text-gray-900 dark:text-gray-100 whitespace-pre-wrap break-words">
        {content}
      </pre>
    </div>
  );
}

export default TerraformPreview;
